/**
 * InvestigationRail — 280↔48px collapsible left rail of active investigations.
 *
 * Sprint-2 Task 2.4 (sprint.md:234). Per-row surface:
 *   INV-<id>  status-tag  headline        E{N} C{N} D{N}*
 *                                          readiness%  confidence▯▯▯
 *
 *   * D count only when drift > 0
 *
 * Rail header: INVESTIGATIONS · N  +  + New  [collapse]
 *
 * Collapsed state (48px): dot-per-investigation stack per master
 * line 953. Screen-reader-announces "Rail collapsed to N investigations"
 * per sprint.md AC-8.
 */

import { useId } from 'react';
import type { ReactNode } from 'react';

export interface InvestigationSummary {
  readonly id: string;
  readonly status: 'open' | 'reviewing' | 'ready' | 'closed' | 'blocked';
  readonly headline: string;
  readonly evidenceCount: number;
  readonly claimCount: number;
  readonly driftCount: number;
  /** 0–1. Rendered as percent. */
  readonly readiness: number;
  /** 0–1. Rendered as 5-segment bar. */
  readonly confidence: number;
  /** Accent hue for collapsed-state dot + active-row stripe. */
  readonly accentVar?: string;
}

export interface InvestigationRailProps {
  investigations: readonly InvestigationSummary[];
  collapsed: boolean;
  onToggleCollapse: () => void;
  onSelectInvestigation?: (id: string) => void;
  onNewInvestigation?: () => void;
  /** id of currently-open investigation (drives active highlight). */
  activeId?: string;
  /** Optional footer content (FR-W-1: Suggestions widget). Hidden when collapsed. */
  footer?: ReactNode;
  className?: string;
}

export function InvestigationRail({
  investigations,
  collapsed,
  onToggleCollapse,
  onSelectInvestigation,
  onNewInvestigation,
  activeId,
  footer,
  className,
}: InvestigationRailProps) {
  const liveId = useId();
  const n = investigations.length;

  return (
    <aside
      role="navigation"
      aria-label="Investigations rail"
      className={className}
      style={{
        width: collapsed ? 48 : 280,
        flexShrink: 0,
        background: 'var(--cd-bg-panel)',
        borderRight: '1px solid var(--cd-seam)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width .14s var(--cd-ease-out)',
        position: 'relative',
        fontFamily: 'var(--cd-font-sans)',
      }}
    >
      {/* Corner ticks (HUD accent) */}
      <CornerTick position="tl" />
      <CornerTick position="bl" />

      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          padding: collapsed ? '10px 0' : '10px 14px',
          borderBottom: '1px solid var(--cd-seam)',
          background: 'var(--cd-bg-card-header)',
          gap: 6,
        }}
      >
        {!collapsed && (
          <>
            <span
              style={{
                fontFamily: 'var(--cd-font-mono)',
                fontSize: 10,
                color: 'var(--cd-fg)',
                letterSpacing: '0.14em',
                fontWeight: 500,
                textTransform: 'uppercase',
              }}
            >
              Investigations
              <span
                aria-hidden="true"
                style={{ color: 'var(--cd-fg-muted)', marginLeft: 6, fontVariantNumeric: 'tabular-nums' }}
              >
                · {n}
              </span>
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {onNewInvestigation && (
                <IconButton
                  onClick={onNewInvestigation}
                  aria-label="New investigation"
                  title="New investigation"
                >
                  +
                </IconButton>
              )}
              <IconButton
                onClick={onToggleCollapse}
                aria-label="Collapse investigations rail"
                aria-expanded
                title="Collapse rail"
              >
                ⇤
              </IconButton>
            </div>
          </>
        )}
        {collapsed && (
          <IconButton
            onClick={onToggleCollapse}
            aria-label="Expand investigations rail"
            aria-expanded={false}
            title="Expand rail"
          >
            ⇥
          </IconButton>
        )}
      </div>

      {/* Live region — announces collapse state per AC-8 */}
      <div
        id={liveId}
        role="status"
        aria-live="polite"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {collapsed ? `Rail collapsed to ${n} investigations` : `Rail expanded with ${n} investigations`}
      </div>

      {/* Body — rows or dot-stack */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {collapsed ? (
          <CollapsedDotStack
            investigations={investigations}
            activeId={activeId}
            onSelect={onSelectInvestigation}
          />
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {investigations.map((inv) => (
              <InvestigationRow
                key={inv.id}
                investigation={inv}
                active={inv.id === activeId}
                onSelect={onSelectInvestigation}
              />
            ))}
          </ul>
        )}
      </div>

      {/* FR-W-1 (cycle-105.8): optional footer slot (Suggestions widget). Hidden
          when collapsed (48px) to match the dot-stack affordance. */}
      {!collapsed && footer && (
        <div
          style={{
            flexShrink: 0,
            maxHeight: '40%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            borderTop: '1px solid var(--cd-seam)',
          }}
        >
          {footer}
        </div>
      )}
    </aside>
  );
}

// ── Row ──────────────────────────────────────────────────────────────────

interface InvestigationRowProps {
  investigation: InvestigationSummary;
  active: boolean;
  onSelect?: (id: string) => void;
}

const STATUS_LABELS: Record<InvestigationSummary['status'], string> = {
  open: 'OPEN',
  reviewing: 'REVIEW',
  ready: 'READY',
  closed: 'CLOSED',
  blocked: 'BLOCKED',
};

function InvestigationRow({ investigation: inv, active, onSelect }: InvestigationRowProps) {
  const driftShown = inv.driftCount > 0;
  const accent = inv.accentVar ?? '--cd-sig-iris';
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect?.(inv.id)}
        aria-label={`Investigation ${inv.id}: ${inv.headline}`}
        aria-current={active ? 'true' : undefined}
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          padding: '10px 12px',
          background: active ? 'var(--cd-bg-hover)' : 'transparent',
          border: 'none',
          borderLeft: active ? `2px solid var(${accent})` : '2px solid transparent',
          borderBottom: '1px solid var(--cd-seam)',
          textAlign: 'left',
          cursor: 'pointer',
          color: 'var(--cd-fg)',
          transition: 'background .12s',
        }}
      >
        {/* Line 1: ID + status tag */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              fontFamily: 'var(--cd-font-mono)',
              fontSize: 9.5,
              letterSpacing: '0.08em',
              color: 'var(--cd-fg-secondary)',
            }}
          >
            {inv.id}
          </span>
          <StatusTag status={inv.status} />
        </div>
        {/* Line 2: headline */}
        <div
          style={{
            fontSize: 12.5,
            lineHeight: 1.35,
            color: 'var(--cd-fg)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {inv.headline}
        </div>
        {/* Line 3: E/C/D counts + readiness */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontFamily: 'var(--cd-font-mono)',
            fontSize: 9.5,
            color: 'var(--cd-fg-muted)',
            letterSpacing: '0.06em',
          }}
        >
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            E{inv.evidenceCount} C{inv.claimCount}
            {driftShown && (
              <>
                {' '}
                <span style={{ color: 'var(--cd-sig-amber)' }}>D{inv.driftCount}</span>
              </>
            )}
          </span>
          <span style={{ color: 'var(--cd-fg-secondary)', fontVariantNumeric: 'tabular-nums' }}>
            {Math.round(inv.readiness * 100)}%
          </span>
        </div>
        {/* Line 4: confidence bar */}
        <ConfidenceBar value={inv.confidence} accentVar={accent} />
      </button>
    </li>
  );
}

// ── Status tag ──────────────────────────────────────────────────────────

function StatusTag({ status }: { status: InvestigationSummary['status'] }) {
  const hueMap: Record<InvestigationSummary['status'], string> = {
    open: '--cd-sig-azure',
    reviewing: '--cd-sig-iris',
    ready: '--cd-sig-mint',
    closed: '--cd-fg-muted',
    blocked: '--cd-sig-ember',
  };
  return (
    <span
      style={{
        fontFamily: 'var(--cd-font-mono)',
        fontSize: 8.5,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: `var(${hueMap[status]})`,
        border: `1px solid var(${hueMap[status]})`,
        borderRadius: 2,
        padding: '1px 4px',
        lineHeight: 1.2,
      }}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

// ── Confidence bar (5 segments) ─────────────────────────────────────────

function ConfidenceBar({ value, accentVar }: { value: number; accentVar: string }) {
  const segments = 5;
  const filled = Math.round(value * segments);
  return (
    <div
      aria-label={`Confidence ${Math.round(value * 100)}%`}
      style={{ display: 'flex', gap: 2, alignItems: 'center' }}
    >
      {Array.from({ length: segments }).map((_, i) => (
        <span
          key={i}
          aria-hidden="true"
          style={{
            width: 14,
            height: 3,
            borderRadius: 1,
            background: i < filled ? `var(${accentVar})` : 'var(--cd-bg-inset)',
          }}
        />
      ))}
    </div>
  );
}

// ── Collapsed dot-stack ────────────────────────────────────────────────

interface CollapsedDotStackProps {
  investigations: readonly InvestigationSummary[];
  activeId?: string;
  onSelect?: (id: string) => void;
}

function CollapsedDotStack({ investigations, activeId, onSelect }: CollapsedDotStackProps) {
  return (
    <ol
      style={{
        listStyle: 'none',
        margin: 0,
        padding: '8px 0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
      }}
    >
      {investigations.map((inv) => {
        const active = inv.id === activeId;
        const accent = inv.accentVar ?? '--cd-sig-iris';
        return (
          <li key={inv.id}>
            <button
              type="button"
              onClick={() => onSelect?.(inv.id)}
              title={`${inv.id}: ${inv.headline}`}
              aria-label={`Investigation ${inv.id}: ${inv.headline}`}
              aria-current={active ? 'true' : undefined}
              style={{
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: active ? 10 : 7,
                  height: active ? 10 : 7,
                  borderRadius: '50%',
                  background: `var(${accent})`,
                  opacity: active ? 1 : 0.55,
                  transition: 'opacity .12s, width .12s, height .12s',
                }}
              />
            </button>
          </li>
        );
      })}
    </ol>
  );
}

// ── Utility: corner tick + icon button ─────────────────────────────────

function CornerTick({ position }: { position: 'tl' | 'bl' }) {
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        left: 0,
        [position === 'tl' ? 'top' : 'bottom']: 0,
        width: 10,
        height: 10,
        borderLeft: '1px solid var(--cd-border-strong)',
        [position === 'tl' ? 'borderTop' : 'borderBottom']: '1px solid var(--cd-border-strong)',
      }}
    />
  );
}

function IconButton({
  children,
  onClick,
  ...aria
}: {
  children: ReactNode;
  onClick: () => void;
  'aria-label': string;
  'aria-expanded'?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      {...aria}
      style={{
        width: 22,
        height: 22,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent',
        border: '1px solid var(--cd-border)',
        borderRadius: 'var(--cd-radius)',
        color: 'var(--cd-fg-muted)',
        cursor: 'pointer',
        fontFamily: 'var(--cd-font-mono)',
        fontSize: 11,
        padding: 0,
        transition: 'color .12s, border-color .12s',
      }}
    >
      {children}
    </button>
  );
}
